import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tab,
  Tabs,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch } from 'react-redux';
import { dispatchMessage, dispatchException } from 'helper/snackbar';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import i18next from 'i18next';
import {
  FLUSH_CACHE_MUTATION,
  RESET_WIDGET_FORM_STATE_MUTATION,
  CACHE_STATS_QUERY,
  SYSTEM_STATS_QUERY,
  CANCEL_CACHE_REBUILD_MUTATION,
  SEED_PRODUCTS_MUTATION,
  SEED_PRODUCTS_PROGRESS_QUERY,
  CANCEL_SEED_PRODUCTS_MUTATION,
  SEED_MISSING_TEXTS_MUTATION,
  SEED_MISSING_TEXTS_PROGRESS_QUERY,
  CANCEL_SEED_MISSING_TEXTS_MUTATION,
  DEBUG_CACHE_DATA_QUERY,
  DEBUG_REDIS_SCAN_KEYS_QUERY,
  DEBUG_REDIS_KEY_LOOKUP_QUERY,
  ADMIN_GLOBAL_SETTINGS_QUERY,
  ADMIN_UPDATE_GLOBAL_SETTINGS_MUTATION,
} from './gql';
import { userSelector } from 'helper/security';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import Switch from '@mui/material/Switch';
import { MeetingPackageImportContent } from 'pages/meetingpackage';
import { useSearchParams } from 'react-router-dom';
import ApiAuditLogSection from './ApiAuditLogSection';
import { CacheDebugSourcesTable } from './CacheDebugSourcesTable';

const COLORS = {
  cached: '#4caf50',
  uncached: '#e0e0e0',
  preparation: '#ff9800',
};

interface ServiceStats {
  serviceName: string;
  heapUsed: number;
  heapUsedHuman: string;
  heapTotal: number;
  heapTotalHuman: string;
  rss: number;
  rssHuman: string;
  maxOldSpaceSize: number;
  maxOldSpaceSizeHuman: string;
  cpuPercent: number;
  uptime: number;
  uptimeHuman: string;
  online: boolean;
}

const CacheStatRow = ({
  label,
  cached,
  total,
  percentage,
  source,
}: {
  label: string;
  cached: number;
  total: number;
  percentage: number;
  source?: string;
}) => (
  <Box sx={{ width: '100%', mb: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" fontWeight="medium">
          {label}
        </Typography>
        {source && (
          <Chip
            label={source === 'DB' ? 'DB' : 'Redis'}
            color={source === 'DB' ? 'info' : 'default'}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Box>
      <Typography variant="body2" color="textSecondary">
        {cached.toLocaleString()} / {total.toLocaleString()} ({Math.round(percentage)}%)
      </Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={percentage}
      sx={{
        height: 8,
        borderRadius: 4,
        backgroundColor: COLORS.uncached,
        '& .MuiLinearProgress-bar': {
          backgroundColor: percentage === 100 ? COLORS.cached : COLORS.preparation,
        },
      }}
    />
  </Box>
);

export default function SystemTab() {
  const dispatch = useDispatch();
  const user = userSelector();
  const [systemSearchParams, setSystemSearchParams] = useSearchParams();
  const requestedSystemSection = systemSearchParams.get('section');
  const systemSection =
    (user?.isRoot &&
      (requestedSystemSection === 'overview' ||
        requestedSystemSection === 'maintenance' ||
        requestedSystemSection === 'cache' ||
        requestedSystemSection === 'audit')) ||
    (requestedSystemSection === 'meeting-package' && user?.isSeminargo)
      ? requestedSystemSection
      : user?.isRoot
        ? 'overview'
        : 'meeting-package';
  const showOverview = systemSection === 'overview';
  const showMaintenance = systemSection === 'maintenance';
  const showAudit = systemSection === 'audit';
  const systemSectionTitle = String(i18next.t(`system-section-${systemSection}`));
  const [flushCacheMutation, { loading }] = useMutation(FLUSH_CACHE_MUTATION);
  const [resetWidgetFormStateMutation, { loading: resetLoading }] = useMutation(RESET_WIDGET_FORM_STATE_MUTATION);
  const [cancelCacheRebuildMutation, { loading: cancelLoading }] = useMutation(CANCEL_CACHE_REBUILD_MUTATION);
  const [seedProductsMutation, { loading: seedLoading }] = useMutation(SEED_PRODUCTS_MUTATION);
  const [cancelSeedProductsMutation, { loading: cancelSeedLoading }] = useMutation(CANCEL_SEED_PRODUCTS_MUTATION);
  const [seedMissingTextsMutation, { loading: seedMissingTextsLoading }] = useMutation(SEED_MISSING_TEXTS_MUTATION);
  const [cancelSeedMissingTextsMutation, { loading: cancelSeedMissingTextsLoading }] = useMutation(CANCEL_SEED_MISSING_TEXTS_MUTATION);

  const [flushDialogOpen, setFlushDialogOpen] = useState(false);
  const [rebuildAfterFlush, setRebuildAfterFlush] = useState(true);
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [resetSeeding, setResetSeeding] = useState(false);
  const [seedMissingTextsDialogOpen, setSeedMissingTextsDialogOpen] = useState(false);
  const [resetMissingTextSeeding, setResetMissingTextSeeding] = useState(false);
  const [disableApprovalDialogOpen, setDisableApprovalDialogOpen] = useState(false);

  // Global settings (offer seminargo approval toggle)
  const { data: globalSettingsData, refetch: refetchGlobalSettings } = useQuery(ADMIN_GLOBAL_SETTINGS_QUERY, {
    skip: !user?.isRoot || !showMaintenance,
    fetchPolicy: 'network-only',
  });
  const [updateGlobalSettingsMutation, { loading: updateGlobalSettingsLoading }] = useMutation(ADMIN_UPDATE_GLOBAL_SETTINGS_MUTATION);

  // Cache Debug Tool state
  const [debugHotelInput, setDebugHotelInput] = useState('');
  const [debugMaxBytes, setDebugMaxBytes] = useState('200000');
  const [debugCacheData, { data: debugData, loading: debugLoading }] = useLazyQuery(DEBUG_CACHE_DATA_QUERY, {
    fetchPolicy: 'network-only',
  });

  // Redis Scan Keys state
  const [scanPattern, setScanPattern] = useState('');
  const [scanCursor, setScanCursor] = useState<string | undefined>(undefined);
  const [scanLimit, setScanLimit] = useState('100');
  const [debugScanKeys, { data: scanData, loading: scanLoading }] = useLazyQuery(DEBUG_REDIS_SCAN_KEYS_QUERY, {
    fetchPolicy: 'network-only',
  });

  // Redis Key Lookup state
  const [lookupKey, setLookupKey] = useState('');
  const [lookupMaxBytes, setLookupMaxBytes] = useState('200000');
  const [debugKeyLookup, { data: lookupData, loading: lookupLoading }] = useLazyQuery(DEBUG_REDIS_KEY_LOOKUP_QUERY, {
    fetchPolicy: 'network-only',
  });

  const {
    data: cacheStatsData,
    startPolling,
    stopPolling,
  } = useQuery(CACHE_STATS_QUERY, {
    skip: !showOverview && !showMaintenance,
    fetchPolicy: 'network-only',
  });

  const isRebuilding = cacheStatsData?.cacheStats?.isRebuilding ?? false;

  const { data: systemStatsData } = useQuery(SYSTEM_STATS_QUERY, {
    skip: !showOverview,
    fetchPolicy: 'network-only',
    pollInterval: 30000, // refresh every 30 seconds
  });

  const {
    data: seedProgressData,
    startPolling: startSeedPolling,
    stopPolling: stopSeedPolling,
    refetch: refetchSeedProgress,
  } = useQuery(SEED_PRODUCTS_PROGRESS_QUERY, {
    skip: !showMaintenance,
    fetchPolicy: 'network-only',
  });
  const {
    data: seedMissingTextsProgressData,
    startPolling: startSeedMissingTextsPolling,
    stopPolling: stopSeedMissingTextsPolling,
    refetch: refetchSeedMissingTextsProgress,
  } = useQuery(SEED_MISSING_TEXTS_PROGRESS_QUERY, {
    skip: !showMaintenance,
    fetchPolicy: 'network-only',
  });

  const isSeeding = seedProgressData?.seedProductsProgress?.isRunning ?? false;
  const isMissingTextsSeeding = seedMissingTextsProgressData?.seedMissingProductTextsProgress?.isRunning ?? false;

  useEffect(() => {
    if (isRebuilding) {
      startPolling(5000);
    } else {
      stopPolling();
    }
  }, [isRebuilding, startPolling, stopPolling]);

  useEffect(() => {
    if (isSeeding) {
      startSeedPolling(2000);
    } else {
      stopSeedPolling();
    }
  }, [isSeeding, startSeedPolling, stopSeedPolling]);

  useEffect(() => {
    if (isMissingTextsSeeding) {
      startSeedMissingTextsPolling(2000);
    } else {
      stopSeedMissingTextsPolling();
    }
  }, [isMissingTextsSeeding, startSeedMissingTextsPolling, stopSeedMissingTextsPolling]);

  const handleFlushCache = async () => {
    try {
      await flushCacheMutation({ variables: { rebuild: rebuildAfterFlush } });
      if (rebuildAfterFlush) {
        startPolling(5000);
        dispatchMessage(dispatch, String(i18next.t('system-cache-flushed-rebuilding')));
      } else {
        dispatchMessage(dispatch, String(i18next.t('system-cache-flushed')));
      }
      setFlushDialogOpen(false);
      setRebuildAfterFlush(false);
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const handleResetWidgetFormState = async () => {
    try {
      await resetWidgetFormStateMutation();
      dispatchMessage(dispatch, String(i18next.t('system-widget-form-reset')));
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const handleCancelCacheRebuild = async () => {
    try {
      await cancelCacheRebuildMutation();
      stopPolling();
      dispatchMessage(dispatch, String(i18next.t('system-cache-rebuild-cancelled')));
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const handleSeedProducts = async () => {
    try {
      await seedProductsMutation({ variables: { reset: resetSeeding } });
      // Refetch progress to get updated status
      await refetchSeedProgress();
      startSeedPolling(2000);
      dispatchMessage(dispatch, String(i18next.t('system-seed-products-running')));
      setSeedDialogOpen(false);
      setResetSeeding(false);
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const handleCancelSeedProducts = async () => {
    try {
      await cancelSeedProductsMutation();
      stopSeedPolling();
      // Refetch progress to update UI immediately
      await refetchSeedProgress();
      dispatchMessage(dispatch, String(i18next.t('system-import-cancelled')));
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const handleSeedMissingTexts = async () => {
    try {
      await seedMissingTextsMutation({ variables: { reset: resetMissingTextSeeding } });
      await refetchSeedMissingTextsProgress();
      startSeedMissingTextsPolling(2000);
      dispatchMessage(dispatch, String(i18next.t('system-seed-texts-running')));
      setSeedMissingTextsDialogOpen(false);
      setResetMissingTextSeeding(false);
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const handleCancelSeedMissingTexts = async () => {
    try {
      await cancelSeedMissingTextsMutation();
      stopSeedMissingTextsPolling();
      await refetchSeedMissingTextsProgress();
      dispatchMessage(dispatch, String(i18next.t('system-text-import-cancelled')));
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const handleDebugCacheQuery = async () => {
    try {
      const input = debugHotelInput.trim();
      if (!input) {
        dispatchException(dispatch, new Error(String(i18next.t('system-debug-hotel-or-ref-required'))));
        return;
      }

      const maxBytesNum = debugMaxBytes.trim() ? parseInt(debugMaxBytes.trim(), 10) : undefined;

      // Check if input is a number (hotel ID) or string (refCode)
      const isNumeric = /^\d+$/.test(input);
      const hotelIdNum = isNumeric ? parseInt(input, 10) : undefined;
      const refCodeStr = isNumeric ? undefined : input;

      await debugCacheData({
        variables: {
          hotelId: hotelIdNum,
          refCode: refCodeStr,
          maxBytes: maxBytesNum,
        },
      });
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const handleDebugScanKeys = async () => {
    try {
      const pattern = scanPattern.trim();
      if (!pattern) {
        dispatchException(dispatch, new Error(String(i18next.t('system-debug-pattern-required'))));
        return;
      }

      const limitNum = scanLimit.trim() ? parseInt(scanLimit.trim(), 10) : undefined;

      await debugScanKeys({
        variables: {
          pattern,
          cursor: scanCursor || undefined,
          limit: limitNum,
        },
      });
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const handleDebugKeyLookup = async () => {
    try {
      const key = lookupKey.trim();
      if (!key) {
        dispatchException(dispatch, new Error(String(i18next.t('system-debug-key-required'))));
        return;
      }

      const maxBytesNum = lookupMaxBytes.trim() ? parseInt(lookupMaxBytes.trim(), 10) : undefined;

      await debugKeyLookup({
        variables: {
          key,
          maxBytes: maxBytesNum,
        },
      });
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const handleToggleOfferSeminargoApproval = async (enabled: boolean) => {
    if (!enabled && globalSettingsData?.globalSettings?.offerSeminargoApprovalEnabled) {
      // Disabling — show confirmation dialog
      setDisableApprovalDialogOpen(true);
      return;
    }
    try {
      await updateGlobalSettingsMutation({ variables: { offerSeminargoApprovalEnabled: enabled } });
      await refetchGlobalSettings();
      dispatchMessage(dispatch, String(i18next.t('administration-offer-seminargo-approval-updated')));
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const handleConfirmDisableApproval = async () => {
    try {
      await updateGlobalSettingsMutation({ variables: { offerSeminargoApprovalEnabled: false } });
      await refetchGlobalSettings();
      setDisableApprovalDialogOpen(false);
      dispatchMessage(dispatch, String(i18next.t('administration-offer-seminargo-approval-updated')));
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const cacheStats = cacheStatsData?.cacheStats;
  const systemStats = systemStatsData?.systemStats;
  const serviceStats = systemStatsData?.serviceStats;
  const seedProgress = seedProgressData?.seedProductsProgress;
  const seedMissingTextsProgress = seedMissingTextsProgressData?.seedMissingProductTextsProgress;

  return (
    <>
      <Helmet>
        <title>
          {String(i18next.t('system'))} / {systemSectionTitle}
        </title>
      </Helmet>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={systemSection}
          onChange={(_event, value: string) =>
            setSystemSearchParams(value === 'overview' ? {} : { section: value }, {
              replace: true,
            })
          }
          variant="scrollable"
          scrollButtons="auto"
        >
          {user?.isRoot && <Tab value="overview" label={i18next.t('system-section-overview')} />}
          {user?.isRoot && <Tab value="maintenance" label={i18next.t('system-section-maintenance')} />}
          {user?.isRoot && <Tab value="cache" label={i18next.t('system-section-cache')} />}
          {user?.isRoot && <Tab value="audit" label={i18next.t('system-section-audit')} />}
          {user?.isSeminargo && <Tab value="meeting-package" label={i18next.t('system-section-meeting-package')} />}
        </Tabs>
      </Box>
      <Grid container spacing={3}>
        {user?.isRoot && showAudit && (
          <Grid item xs={12}>
            <ApiAuditLogSection />
          </Grid>
        )}
        {user?.isSeminargo && systemSection === 'meeting-package' && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <MeetingPackageImportContent />
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={12} sx={{ display: systemSection === 'overview' ? 'block' : 'none' }}>
          <Card>
            <CardHeader
              title={String(i18next.t('system-cache-status-title'))}
              action={
                isRebuilding && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<CancelIcon />}
                    disabled={cancelLoading}
                    onClick={handleCancelCacheRebuild}
                  >
                    {i18next.t('cancel')}
                  </Button>
                )
              }
            />
            <CardContent>
              {cacheStats && (
                <>
                  {isRebuilding && (
                    <Box sx={{ mb: 4, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="subtitle1" gutterBottom color="primary" fontWeight="bold">
                        {i18next.t('system-cache-rebuilding')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <LinearProgress variant="determinate" value={cacheStats.preparationProgress || 0} sx={{ height: 10, borderRadius: 5 }} />
                        </Box>
                        <Typography variant="body2" fontWeight="bold" minWidth={50} textAlign="right">
                          {Math.round(cacheStats.preparationProgress || 0)}%
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                        {i18next.t('system-cache-please-wait')}
                      </Typography>
                    </Box>
                  )}

                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                        {i18next.t('system-main-data')}
                      </Typography>
                      <CacheStatRow
                        label={String(i18next.t('system-hotels'))}
                        cached={cacheStats.hotelsCached}
                        total={cacheStats.hotelsTotal}
                        percentage={cacheStats.hotelsPercentage}
                        source={cacheStats.hotelsSource}
                      />
                      <CacheStatRow
                        label={String(i18next.t('system-calculators'))}
                        cached={cacheStats.calculatorsCached}
                        total={cacheStats.calculatorsTotal}
                        percentage={cacheStats.calculatorsPercentage}
                        source={cacheStats.calculatorsSource}
                      />
                      <CacheStatRow
                        label={String(i18next.t('system-products'))}
                        cached={cacheStats.productsCached}
                        total={cacheStats.productsTotal}
                        percentage={cacheStats.productsPercentage}
                        source={cacheStats.productsSource}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                        {i18next.t('system-pricelist-validation')}
                      </Typography>
                      <CacheStatRow
                        label={String(i18next.t('system-lister-validation'))}
                        cached={cacheStats.listerStatusCached}
                        total={cacheStats.listerStatusTotal}
                        percentage={cacheStats.listerStatusPercentage}
                        source={cacheStats.listerStatusSource}
                      />
                      <CacheStatRow
                        label={String(i18next.t('system-widget-validation'))}
                        cached={cacheStats.widgetStatusCached}
                        total={cacheStats.widgetStatusTotal}
                        percentage={cacheStats.widgetStatusPercentage}
                        source={cacheStats.widgetStatusSource}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sx={{ display: systemSection === 'overview' ? 'block' : 'none' }}>
          <Card>
            <CardHeader title={String(i18next.t('system-status-title'))} />
            <CardContent>
              {systemStats && (
                <Grid container spacing={4}>
                  <Grid item xs>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <StorageIcon sx={{ mr: 1, color: 'error.main' }} />
                      <Typography variant="h6">{i18next.t('system-status-redis-heading')}</Typography>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        {i18next.t('memory')}
                      </Typography>
                      <Typography variant="body1">
                        {systemStats.redis.usedMemoryHuman}
                        {systemStats.redis.maxMemory > 0 ? ` / ${systemStats.redis.maxMemoryHuman}` : ` (${i18next.t('system-unlimited')})`}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {i18next.t('connections')}
                      </Typography>
                      <Typography variant="body1">{systemStats.redis.connectedClients}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">{i18next.t('system-status-mysql-heading')}</Typography>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        {i18next.t('system-mysql-buffer-pool')}
                      </Typography>
                      <Typography variant="body1">{systemStats.mysql.bufferPoolSizeHuman}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {i18next.t('connections')}
                      </Typography>
                      <Typography variant="body1">
                        {systemStats.mysql.threadsConnected} / {systemStats.mysql.maxConnections}
                      </Typography>
                    </Box>
                  </Grid>
                  {serviceStats?.map((service: ServiceStats) => (
                    <Grid item xs key={service.serviceName}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <MemoryIcon sx={{ mr: 1, color: service.online ? 'success.main' : 'text.disabled' }} />
                        <Typography variant="h6">
                          {service.serviceName === 'backend'
                            ? i18next.t('system-service-backoffice-api')
                            : service.serviceName === 'pricelist'
                              ? i18next.t('system-service-public-api')
                              : i18next.t('system-service-scheduler')}
                        </Typography>
                        {!service.online && (
                          <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                            ({i18next.t('offline')})
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          {i18next.t('system-heap-max')}
                        </Typography>
                        <Typography variant="body1" color={service.online ? 'text.primary' : 'text.disabled'}>
                          {service.heapUsedHuman} / {service.maxOldSpaceSizeHuman}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          {i18next.t('cpu')}
                        </Typography>
                        <Typography variant="body1" color={service.online ? 'text.primary' : 'text.disabled'}>
                          {Math.max(0, Math.min(100, service.cpuPercent)).toFixed(1)}%
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {i18next.t('uptime')}
                        </Typography>
                        <Typography variant="body1" color={service.online ? 'text.primary' : 'text.disabled'}>
                          {service.uptimeHuman}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sx={{ display: systemSection === 'maintenance' ? 'block' : 'none' }}>
          <Card>
            <CardHeader title={String(i18next.t('system-admin-title'))} />
            <CardContent>
              <Box>
                <Typography variant="body1" gutterBottom>
                  {i18next.t('system-redis-cache')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {i18next.t('system-redis-cache-description')}
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<DeleteIcon />}
                  disabled={loading || isRebuilding}
                  onClick={() => setFlushDialogOpen(true)}
                >
                  {i18next.t('system-cache-clear-title')}
                </Button>
              </Box>
              <Box sx={{ mt: 4 }}>
                <Typography variant="body1" gutterBottom>
                  {i18next.t('system-lister-widget')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {i18next.t('system-lister-widget-description')}
                </Typography>
                <ConfirmationButton
                  variant="contained"
                  color="secondary"
                  startIcon={<RefreshIcon />}
                  disabled={resetLoading}
                  confirmationTitle={String(i18next.t('system-widget-reset-title'))}
                  confirmationQuestion={String(i18next.t('system-widget-reset-confirm'))}
                  onConfirm={handleResetWidgetFormState}
                >
                  {i18next.t('system-widget-reset-title')}
                </ConfirmationButton>
              </Box>
              <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body1" gutterBottom>
                    {i18next.t('system-services-pricelists')}
                  </Typography>
                  {isSeeding && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<CancelIcon />}
                      disabled={cancelSeedLoading}
                      onClick={handleCancelSeedProducts}
                    >
                      {i18next.t('cancel')}
                    </Button>
                  )}
                </Box>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {i18next.t('system-seed-products-description')}
                </Typography>
                {isSeeding && seedProgress && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom color="primary" fontWeight="bold">
                      {i18next.t('system-import-running')}
                    </Typography>
                    {seedProgress.activeSpaces && seedProgress.activeSpaces.length > 0 && (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {i18next.t('system-processing-spaces', { count: seedProgress.activeSpaces.length })}:{' '}
                        {seedProgress.activeSpaces.slice(0, 5).join(', ')}
                        {seedProgress.activeSpaces.length > 5 && ` +${seedProgress.activeSpaces.length - 5} weitere`}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <LinearProgress variant="determinate" value={seedProgress.progress || 0} sx={{ height: 10, borderRadius: 5 }} />
                      </Box>
                      <Typography variant="body2" fontWeight="bold" minWidth={50} textAlign="right">
                        {Math.round(seedProgress.progress || 0)}%
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      {seedProgress.spacesCompleted} / {seedProgress.spacesTotal} {i18next.t('system-spaces-completed')}
                    </Typography>
                    {seedProgress.error && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {i18next.t('error')}: {seedProgress.error}
                      </Typography>
                    )}
                  </Box>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlayArrowIcon />}
                  disabled={seedLoading || isSeeding || isMissingTextsSeeding}
                  onClick={() => setSeedDialogOpen(true)}
                >
                  {i18next.t('system-services-pricelists-import')}
                </Button>
              </Box>
              <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body1" gutterBottom>
                    {i18next.t('system-missing-producttexts')}
                  </Typography>
                  {isMissingTextsSeeding && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<CancelIcon />}
                      disabled={cancelSeedMissingTextsLoading}
                      onClick={handleCancelSeedMissingTexts}
                    >
                      {i18next.t('cancel')}
                    </Button>
                  )}
                </Box>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {i18next.t('system-seed-texts-description')}
                </Typography>
                {isMissingTextsSeeding && seedMissingTextsProgress && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom color="primary" fontWeight="bold">
                      {i18next.t('system-text-import-running')}
                    </Typography>
                    {seedMissingTextsProgress.activeSpaces && seedMissingTextsProgress.activeSpaces.length > 0 && (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {i18next.t('system-processing-spaces', { count: seedMissingTextsProgress.activeSpaces.length })}:{' '}
                        {seedMissingTextsProgress.activeSpaces.slice(0, 5).join(', ')}
                        {seedMissingTextsProgress.activeSpaces.length > 5 && ` +${seedMissingTextsProgress.activeSpaces.length - 5} weitere`}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <LinearProgress variant="determinate" value={seedMissingTextsProgress.progress || 0} sx={{ height: 10, borderRadius: 5 }} />
                      </Box>
                      <Typography variant="body2" fontWeight="bold" minWidth={50} textAlign="right">
                        {Math.round(seedMissingTextsProgress.progress || 0)}%
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      {seedMissingTextsProgress.spacesCompleted} / {seedMissingTextsProgress.spacesTotal} {i18next.t('system-spaces-completed')}
                    </Typography>
                    {seedMissingTextsProgress.error && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {i18next.t('error')}: {seedMissingTextsProgress.error}
                      </Typography>
                    )}
                  </Box>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlayArrowIcon />}
                  disabled={seedMissingTextsLoading || isMissingTextsSeeding || isSeeding}
                  onClick={() => setSeedMissingTextsDialogOpen(true)}
                >
                  {i18next.t('system-missing-producttexts-fill')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sx={{ display: systemSection === 'cache' ? 'block' : 'none' }}>
          <Card>
            <CardHeader title={String(i18next.t('system-cache-debug-tool'))} />
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {i18next.t('system-cache-debug-description')}
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={String(i18next.t('system-debug-hotel-or-ref'))}
                      fullWidth
                      value={debugHotelInput}
                      onChange={e => setDebugHotelInput(e.target.value)}
                      size="small"
                      placeholder={String(i18next.t('system-debug-hotel-or-ref-placeholder'))}
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          handleDebugCacheQuery();
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={String(i18next.t('system-debug-max-bytes'))}
                      type="number"
                      fullWidth
                      value={debugMaxBytes}
                      onChange={e => setDebugMaxBytes(e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      startIcon={<SearchIcon />}
                      onClick={handleDebugCacheQuery}
                      disabled={debugLoading}
                    >
                      {i18next.t('system-cache-fetch')}
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {debugData?.debugCacheData && (
                <Box>
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {debugData.debugCacheData.hotelName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      ID: {debugData.debugCacheData.hotelId} | RefCode: {debugData.debugCacheData.hotelRefCode}
                      {debugData.debugCacheData.hotelSpaceId && ` | Space: ${debugData.debugCacheData.hotelSpaceId}`}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      {i18next.t('system-debug-max-bytes')}: {debugData.debugCacheData.meta.maxBytes.toLocaleString()}
                    </Typography>
                  </Box>

                  <CacheDebugSourcesTable sources={debugData.debugCacheData.sources} />

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography>{i18next.t('system-pricelist-lister')}</Typography>
                        <Chip
                          label={debugData.debugCacheData.foundInCache.pricelistLister ? i18next.t('system-cached') : i18next.t('system-not-cached')}
                          color={debugData.debugCacheData.foundInCache.pricelistLister ? 'success' : 'default'}
                          size="small"
                        />
                        {debugData.debugCacheData.meta.pricelistListerTruncated && (
                          <Chip label={String(i18next.t('system-truncated'))} color="warning" size="small" />
                        )}
                        {debugData.debugCacheData.foundInCache.pricelistLister && (
                          <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
                            {debugData.debugCacheData.meta.pricelistListerBytes.toLocaleString()} {i18next.t('bytes')}
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {debugData.debugCacheData.pricelistListerRaw ? (
                        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                          <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                            {debugData.debugCacheData.pricelistListerRaw}
                          </pre>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          {i18next.t('system-not-in-cache')}
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography>{i18next.t('system-pricelist-widget')}</Typography>
                        <Chip
                          label={debugData.debugCacheData.foundInCache.pricelistWidget ? i18next.t('system-cached') : i18next.t('system-not-cached')}
                          color={debugData.debugCacheData.foundInCache.pricelistWidget ? 'success' : 'default'}
                          size="small"
                        />
                        {debugData.debugCacheData.meta.pricelistWidgetTruncated && (
                          <Chip label={String(i18next.t('system-truncated'))} color="warning" size="small" />
                        )}
                        {debugData.debugCacheData.foundInCache.pricelistWidget && (
                          <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
                            {debugData.debugCacheData.meta.pricelistWidgetBytes.toLocaleString()} {i18next.t('bytes')}
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {debugData.debugCacheData.pricelistWidgetRaw ? (
                        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                          <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                            {debugData.debugCacheData.pricelistWidgetRaw}
                          </pre>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          {i18next.t('system-not-in-cache')}
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography>{i18next.t('system-lister-status')}</Typography>
                        <Chip
                          label={debugData.debugCacheData.foundInCache.listerStatus ? i18next.t('system-cached') : i18next.t('system-not-cached')}
                          color={debugData.debugCacheData.foundInCache.listerStatus ? 'success' : 'default'}
                          size="small"
                        />
                        {debugData.debugCacheData.foundInCache.listerStatus && (
                          <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
                            {debugData.debugCacheData.meta.listerStatusBytes.toLocaleString()} {i18next.t('bytes')}
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {debugData.debugCacheData.listerStatus ? (
                        <Typography variant="body1" fontWeight="medium">
                          {debugData.debugCacheData.listerStatus}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Nicht im Cache
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography>{i18next.t('system-widget-status')}</Typography>
                        <Chip
                          label={debugData.debugCacheData.foundInCache.widgetStatus ? 'CACHED' : 'NOT CACHED'}
                          color={debugData.debugCacheData.foundInCache.widgetStatus ? 'success' : 'default'}
                          size="small"
                        />
                        {debugData.debugCacheData.foundInCache.widgetStatus && (
                          <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
                            {debugData.debugCacheData.meta.widgetStatusBytes.toLocaleString()} bytes
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {debugData.debugCacheData.widgetStatus ? (
                        <Typography variant="body1" fontWeight="medium">
                          {debugData.debugCacheData.widgetStatus}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Nicht im Cache
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography>{i18next.t('system-hotel-data')}</Typography>
                        <Chip
                          label={debugData.debugCacheData.foundInCache.hotelData ? 'CACHED' : 'NOT CACHED'}
                          color={debugData.debugCacheData.foundInCache.hotelData ? 'success' : 'default'}
                          size="small"
                        />
                        {debugData.debugCacheData.meta.hotelDataTruncated && <Chip label="TRUNCATED" color="warning" size="small" />}
                        {debugData.debugCacheData.foundInCache.hotelData && (
                          <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
                            {debugData.debugCacheData.meta.hotelDataBytes.toLocaleString()} bytes
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {debugData.debugCacheData.hotelDataJson ? (
                        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                          <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                            {debugData.debugCacheData.hotelDataJson}
                          </pre>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Nicht im Cache
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sx={{ display: systemSection === 'cache' ? 'block' : 'none' }}>
          <Card>
            <CardHeader title={String(i18next.t('system-redis-key-scan'))} />
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {i18next.t('system-redis-key-scan-description')}
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={String(i18next.t('system-pattern'))}
                      fullWidth
                      value={scanPattern}
                      onChange={e => setScanPattern(e.target.value)}
                      size="small"
                      placeholder={String(i18next.t('system-pattern-placeholder'))}
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          handleDebugScanKeys();
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label={String(i18next.t('system-limit'))}
                      type="number"
                      fullWidth
                      value={scanLimit}
                      onChange={e => setScanLimit(e.target.value)}
                      size="small"
                      helperText={String(i18next.t('system-max-keys'))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={String(i18next.t('system-cursor-optional'))}
                      fullWidth
                      value={scanCursor || ''}
                      onChange={e => setScanCursor(e.target.value || undefined)}
                      size="small"
                      placeholder={String(i18next.t('system-cursor-start-placeholder'))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      startIcon={<SearchIcon />}
                      onClick={handleDebugScanKeys}
                      disabled={scanLoading}
                    >
                      {i18next.t('system-scan-keys')}
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {scanData?.debugRedisScanKeys && (
                <Box>
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {i18next.t('system-scan-results')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {i18next.t('system-pattern')}: {scanData.debugRedisScanKeys.pattern} |{' '}
                      {i18next.t('system-found-keys', { count: scanData.debugRedisScanKeys.count })}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      {i18next.t('system-cursor')}: {scanData.debugRedisScanKeys.cursor}{' '}
                      {scanData.debugRedisScanKeys.cursor === '0' && `(${i18next.t('system-complete')})`}
                    </Typography>
                  </Box>

                  {scanData.debugRedisScanKeys.keys.length > 0 ? (
                    <Box sx={{ maxHeight: 400, overflow: 'auto', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      {scanData.debugRedisScanKeys.keys.map((key: string, index: number) => (
                        <Typography
                          key={index}
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            mb: 0.5,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                          onClick={() => {
                            setLookupKey(key);
                            // Scroll to lookup section
                            setTimeout(() => {
                              const lookupSection = document.getElementById('redis-key-lookup');
                              if (lookupSection) {
                                lookupSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }
                            }, 100);
                          }}
                        >
                          {key}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {i18next.t('system-no-keys-found')}
                    </Typography>
                  )}

                  {scanData.debugRedisScanKeys.cursor !== '0' && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setScanCursor(scanData.debugRedisScanKeys.cursor);
                          handleDebugScanKeys();
                        }}
                      >
                        {i18next.t('system-continue-scan')} ({i18next.t('system-cursor')}: {scanData.debugRedisScanKeys.cursor})
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sx={{ display: systemSection === 'cache' ? 'block' : 'none' }}>
          <Card id="redis-key-lookup">
            <CardHeader title={String(i18next.t('system-redis-key-lookup'))} />
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {i18next.t('system-redis-key-lookup-description')}
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={String(i18next.t('system-key'))}
                      fullWidth
                      value={lookupKey}
                      onChange={e => setLookupKey(e.target.value)}
                      size="small"
                      placeholder={String(i18next.t('system-key-placeholder'))}
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          handleDebugKeyLookup();
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={String(i18next.t('system-debug-max-bytes'))}
                      type="number"
                      fullWidth
                      value={lookupMaxBytes}
                      onChange={e => setLookupMaxBytes(e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      startIcon={<SearchIcon />}
                      onClick={handleDebugKeyLookup}
                      disabled={lookupLoading}
                    >
                      {i18next.t('system-lookup-key')}
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {lookupData?.debugRedisKeyLookup && (
                <Box>
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {lookupData.debugRedisKeyLookup.key}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                      <Chip
                        label={lookupData.debugRedisKeyLookup.exists ? i18next.t('system-exists') : i18next.t('system-not-found')}
                        color={lookupData.debugRedisKeyLookup.exists ? 'success' : 'default'}
                        size="small"
                      />
                      {lookupData.debugRedisKeyLookup.type && (
                        <Chip label={`${i18next.t('type')}: ${lookupData.debugRedisKeyLookup.type}`} size="small" />
                      )}
                      {lookupData.debugRedisKeyLookup.ttl !== null && (
                        <Chip
                          label={`${i18next.t('system-ttl')}: ${lookupData.debugRedisKeyLookup.ttl}s`}
                          size="small"
                          color={lookupData.debugRedisKeyLookup.ttl < 60 ? 'warning' : 'default'}
                        />
                      )}
                      {lookupData.debugRedisKeyLookup.exists && (
                        <Typography variant="caption" color="textSecondary">
                          {lookupData.debugRedisKeyLookup.bytes.toLocaleString()} {i18next.t('bytes')}
                        </Typography>
                      )}
                      {lookupData.debugRedisKeyLookup.truncated && (
                        <Chip label={String(i18next.t('system-truncated'))} color="warning" size="small" />
                      )}
                    </Box>
                  </Box>

                  {lookupData.debugRedisKeyLookup.exists && lookupData.debugRedisKeyLookup.value !== null ? (
                    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                      <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {lookupData.debugRedisKeyLookup.value}
                      </pre>
                    </Box>
                  ) : lookupData.debugRedisKeyLookup.exists ? (
                    <Typography variant="body2" color="textSecondary">
                      {i18next.t('system-key-exists-no-value')} ({i18next.t('type')}: {lookupData.debugRedisKeyLookup.type || i18next.t('unknown')})
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {i18next.t('system-key-not-found')}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {user?.isRoot && (
          <Grid item xs={12} sx={{ display: systemSection === 'maintenance' ? 'block' : 'none' }}>
            <Card>
              <CardHeader title={String(i18next.t('administration-settings-title'))} />
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body1" gutterBottom>
                      {i18next.t('administration-offer-seminargo-approval')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {i18next.t('administration-offer-seminargo-approval-description')}
                    </Typography>
                  </Box>
                  <Switch
                    checked={globalSettingsData?.globalSettings?.offerSeminargoApprovalEnabled ?? false}
                    onChange={e => handleToggleOfferSeminargoApproval(e.target.checked)}
                    disabled={updateGlobalSettingsLoading}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Dialog open={flushDialogOpen} onClose={() => setFlushDialogOpen(false)}>
          <DialogTitle>{i18next.t('system-cache-clear-title')}</DialogTitle>
          <DialogContent>
            <Typography paragraph>{i18next.t('system-cache-clear-confirm')}</Typography>
            <FormControlLabel
              control={<Checkbox checked={rebuildAfterFlush} onChange={e => setRebuildAfterFlush(e.target.checked)} />}
              label={String(i18next.t('system-cache-rebuild-after-clear'))}
            />
            {rebuildAfterFlush && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1, ml: 4 }}>
                {i18next.t('system-cache-rebuild-hint')}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFlushDialogOpen(false)} color="secondary" variant="contained">
              {i18next.t('cancel')}
            </Button>
            <Button onClick={handleFlushCache} color="primary" variant="contained" disabled={loading}>
              {i18next.t('confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={seedDialogOpen} onClose={() => setSeedDialogOpen(false)}>
          <DialogTitle>{i18next.t('system-services-pricelists-import')}</DialogTitle>
          <DialogContent>
            <Typography paragraph>{i18next.t('system-seed-products-confirm')}</Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {i18next.t('system-action-imports')}
            </Typography>
            <Typography variant="body2" color="textSecondary" component="ul" sx={{ pl: 2 }}>
              <li>{i18next.t('system-import-item-products')}</li>
              <li>{i18next.t('system-import-item-facilities')}</li>
              <li>{i18next.t('system-import-item-bundles')}</li>
              <li>{i18next.t('system-import-item-servicetypes')}</li>
              <li>{i18next.t('system-import-item-pricelists')}</li>
              <li>{i18next.t('system-import-item-assignments')}</li>
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              {i18next.t('system-seed-products-hint')}
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={resetSeeding} onChange={e => setResetSeeding(e.target.checked)} />}
              label={String(i18next.t('system-restart-ignore-processed-spaces'))}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSeedDialogOpen(false)} color="secondary" variant="contained">
              {i18next.t('cancel')}
            </Button>
            <Button onClick={handleSeedProducts} color="primary" variant="contained" disabled={seedLoading || isMissingTextsSeeding}>
              {i18next.t('confirm')}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={seedMissingTextsDialogOpen} onClose={() => setSeedMissingTextsDialogOpen(false)}>
          <DialogTitle>{i18next.t('system-missing-producttexts-fill')}</DialogTitle>
          <DialogContent>
            <Typography paragraph>{i18next.t('system-seed-texts-confirm')}</Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {i18next.t('system-action-fills-for')}
            </Typography>
            <Typography variant="body2" color="textSecondary" component="ul" sx={{ pl: 2 }}>
              <li>{i18next.t('system-import-item-products')}</li>
              <li>{i18next.t('system-import-item-facilities')}</li>
              <li>{i18next.t('system-import-item-bundles')}</li>
              <li>{i18next.t('system-import-item-servicetypes')}</li>
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              {i18next.t('system-seed-texts-hint')}
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={resetMissingTextSeeding} onChange={e => setResetMissingTextSeeding(e.target.checked)} />}
              label={String(i18next.t('system-restart-ignore-processed-spaces'))}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSeedMissingTextsDialogOpen(false)} color="secondary" variant="contained">
              {i18next.t('cancel')}
            </Button>
            <Button onClick={handleSeedMissingTexts} color="primary" variant="contained" disabled={seedMissingTextsLoading || isSeeding}>
              {i18next.t('confirm')}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={disableApprovalDialogOpen} onClose={() => setDisableApprovalDialogOpen(false)}>
          <DialogTitle>{i18next.t('administration-offer-seminargo-approval')}</DialogTitle>
          <DialogContent>
            <Typography paragraph>{i18next.t('administration-offer-seminargo-approval-disable-warning')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDisableApprovalDialogOpen(false)} color="secondary" variant="contained">
              {i18next.t('cancel')}
            </Button>
            <Button onClick={handleConfirmDisableApproval} color="primary" variant="contained" disabled={updateGlobalSettingsLoading}>
              {i18next.t('confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </>
  );
}
